import { ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader } from '@/design/components/card';

/**
 * The frame every unauthenticated screen shares: sign-in, the forced password change, the
 * authenticator enrolment and the PIN.
 *
 * One frame rather than four because they are one sequence — somebody signing in for the first time
 * walks through three of them in a row — and a layout that moves between the steps reads as four
 * unrelated demands rather than as one setup.
 */
export function AuthCard({
  title,
  description,
  children,
}: Readonly<{
  title: string;
  description: string;
  children: ReactNode;
}>): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md shadow-raised">
        <CardHeader>
          <div className="flex items-center gap-2 text-muted-foreground">
            <ShieldCheck aria-hidden className="size-4" />
            <span className="text-xs font-medium tracking-wide uppercase">{t('app.name')}</span>
          </div>
          {/*
            A real `h1` rather than the card's title slot. Each of these is a whole screen with one
            subject, so it needs a document heading: a screen reader's heading list is how somebody
            finds out what a page is, and a styled `div` is absent from it.
          */}
          <h1 className="text-xl leading-none font-semibold">{title}</h1>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}
